import * as THREE from 'three';
import { loadAsset, measureAsset } from './instancing';
import { assetUrl, isGenerated } from './library';
import type { LibraryAsset } from './types';

// Thumbnail renders for the palette: one small offscreen renderer shared by
// every request, a single frame per asset, cached as a data URL. No animation
// loop — a still is enough to know what the button places, and the editor
// runs inside scenes that already spend the GPU budget.

const SIZE = 320;

let renderer: THREE.WebGLRenderer | null = null;
const cache = new Map<string, Promise<string>>();

function ensureRenderer(): THREE.WebGLRenderer {
    if (!renderer) {
        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(SIZE, SIZE);
        renderer.outputColorSpace = THREE.SRGBColorSpace;
    }
    return renderer;
}

export function assetThumbnail(asset: LibraryAsset): Promise<string> {
    let cached = cache.get(asset.id);
    if (!cached) {
        cached = renderThumbnail(asset);
        cache.set(asset.id, cached);
    }
    return cached;
}

async function renderThumbnail(asset: LibraryAsset): Promise<string> {
    // Generated assets are their own source; see assetSource in editor-core.
    const source = await loadAsset(isGenerated(asset.id) ? asset.id : assetUrl(asset));
    const metrics = measureAsset(source);

    const scene = new THREE.Scene();
    // The model is borrowed from the shared GLTF cache, so it must be returned
    // untouched: clone the wrapper, not the geometry.
    const subject = source.clone();
    subject.applyMatrix4(metrics.rebase);
    scene.add(subject);

    scene.add(new THREE.HemisphereLight(0xffffff, 0x8a7a66, 2.4));
    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(3, 5, 4);
    scene.add(key);

    // Three-quarter view framed on the bounds.
    const radius = metrics.height;
    const camera = new THREE.PerspectiveCamera(35, 1, radius / 100, radius * 10);
    camera.position.set(radius * 1.4, radius * 0.9, radius * 1.4);
    camera.lookAt(0, metrics.height * 0.45, 0);

    const r = ensureRenderer();
    r.render(scene, camera);
    const url = r.domElement.toDataURL('image/png');
    scene.remove(subject);
    return url;
}
